import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { Customer as CustomerData, CustomerInput, customers, Vehicle } from '../api';
import { AppLogins } from '../components/AppLogins';
import { CustomerFields } from './Customers';
import { VehicleTable } from './Vehicles';

// The client card: contact details and the customer's vehicles.
export function Customer() {
  const id = Number(useParams().id);
  const location = useLocation();
  const [data, setData] = useState<{ customer: CustomerData; vehicles: Vehicle[] } | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState<CustomerInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(() => customers.get(id).then(setData).catch((e) => setError(e.message)), [id]);
  useEffect(() => { void load(); }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!f) return;
    setBusy(true);
    setFormError('');
    try {
      await customers.update(id, f);
      setEditing(false);
      setNotice('Saved.');
      void load();
    } catch (err) {
      setFormError((err as Error).message);
    }
    setBusy(false);
  };

  if (error && !data) return <div className="page"><p className="error">{error}</p></div>;
  if (!data) return <div className="page"><p className="muted">Loading…</p></div>;
  const c = data.customer;

  return (
    <div className="page">
      <p><Link to="/customers">← Customers</Link></p>
      <div className="page-head">
        <h1>{c.name}</h1>
        {!editing && <button className="link" onClick={() => { setF({ name: c.name, contactName: c.contactName, phone: c.phone, email: c.email, notes: c.notes }); setEditing(true); setNotice(''); }}>Edit</button>}
      </div>
      {notice && <p className="notice" role="status">{notice}</p>}
      {editing && f ? (
        <form className="card form" onSubmit={submit}>
          <CustomerFields f={f} setF={setF} />
          {formError && <p className="error" role="alert">{formError}</p>}
          <div className="actions">
            <button className="primary" disabled={busy}>Save</button>
            <button type="button" className="link" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      ) : (
        <section className="card">
          <dl className="facts">
            <dt>Contact</dt><dd>{c.contactName ?? '–'}</dd>
            <dt>Phone</dt><dd>{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : '–'}</dd>
            <dt>Email</dt><dd>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '–'}</dd>
          </dl>
          {c.notes && <p className="prewrap">{c.notes}</p>}
        </section>
      )}
      <AppLogins customerId={c.id} />
      <h2>Vehicles ({data.vehicles.length})</h2>
      {data.vehicles.length === 0
        ? <p className="muted">No vehicles yet. Choose this customer when installing a tracker, or under Vehicle → Edit.</p>
        : <VehicleTable rows={data.vehicles} showCustomer={false} back={{ from: location.pathname, fromLabel: c.name }} />}
    </div>
  );
}
